/**
 * Internal Observatory ledger contracts (plan 2026-08-31 U1, pinned contract).
 *
 * The guarded write path for the observatory findings ledger — a single S3
 * object three runtimes write, each owning a different slice of it. These are
 * the shapes the `obs` CLI (U2) and the cloud watcher/Scotty handlers (U4) both
 * speak, so they live here rather than in the backend: one contract, two
 * callers, no drift.
 *
 * Key ownership itself is NOT modelled here — it lives in the backend's
 * `services/observatory/writer-identity.ts` and is enforced server-side, because
 * a client-side copy of the rules would be a second source of truth that a lane
 * could simply not consult.
 */

import { z } from 'zod'

/**
 * The lanes a client may write as. `observer` is deliberately absent: it writes
 * through the tick's own guarded store, not this endpoint.
 *
 * `circuit-breaker` is a different KIND of lane (U7 Gap B): it owns a whole
 * SEPARATE object — `observatory/ledger/circuit-breaker.json` — rather than a
 * slice of the findings ledger, and the server binds it to that one key. Before
 * it existed the breaker had no write path from the image at all, so a cloud run
 * printed its ledger delta and reported itself unpersisted, losing the
 * `unauthorizedSeenCount` / `issueId` its ≥2-passes safety rule depends on.
 *
 * `loop` is the sorting loop's own lane (plan 2026-09-07, KTD2). It owns the
 * top-level `loop` key and nothing else — no header key at all — and unlike the
 * agent lanes it is a deterministic SINGLE writer, so its writes go through a
 * bounded read-recheck-write rather than the terminal 409 the agent lanes get.
 */
export const LedgerWriteLaneSchema = z.enum(['watcher', 'scotty', 'circuit-breaker', 'loop'])
export type LedgerWriteLane = z.infer<typeof LedgerWriteLaneSchema>

/**
 * Optional key override, for U5's dry run against a seeded copy of the ledger.
 * The server constrains it to the ledger's own S3 prefix.
 */
export const LedgerKeyQuerySchema = z.object({
  key: z.string().min(1).optional()
})
export type LedgerKeyQuery = z.infer<typeof LedgerKeyQuerySchema>

/** `GET /internal/observatory/ledger` — `ledger`/`etag` are null when absent. */
export const GetLedgerResponseSchema = z.object({
  etag: z.string().nullable(),
  ledger: z.record(z.string(), z.unknown()).nullable()
})
export type GetLedgerResponse = z.infer<typeof GetLedgerResponseSchema>

/**
 * `POST /internal/observatory/ledger/lane-write`.
 *
 * `patch` carries ONLY the lane's own keys — top-level keys plus an optional
 * `header` object of its own header sub-keys. The server merges it onto the
 * current object; it is not a whole document, and sending one is a 422.
 */
export const LaneWriteRequestSchema = z.object({
  lane: LedgerWriteLaneSchema,
  /**
   * The ETag from the `GET` this patch was built against, or `null` for "the
   * `GET` returned no object". Only the `circuit-breaker` lane can act on the
   * null case — as the create of its own ledger; for the others a null etag
   * simply cannot match, and the write is refused like any other lost race.
   */
  etag: z.string().min(1).nullable(),
  patch: z.record(z.string(), z.unknown()),
  key: z.string().min(1).optional()
})
export type LaneWriteRequest = z.infer<typeof LaneWriteRequestSchema>

/** 200 — the version the write produced. */
export const LaneWriteResponseSchema = z.object({
  etag: z.string().nullable()
})
export type LaneWriteResponse = z.infer<typeof LaneWriteResponseSchema>

/**
 * 409 — someone wrote between the caller's read and this write. Terminal: the
 * caller re-reads, rebuilds its patch and writes again. It never retries as-is.
 */
export const LaneWriteConflictSchema = z.object({
  competingWriter: z.string(),
  currentEtag: z.string().nullable()
})
export type LaneWriteConflict = z.infer<typeof LaneWriteConflictSchema>

/** 422 — the patch named keys outside the lane. Nothing was read or written. */
export const LaneWriteForeignKeysSchema = z.object({
  foreignKeys: z.array(z.string())
})
export type LaneWriteForeignKeys = z.infer<typeof LaneWriteForeignKeysSchema>

/**
 * `POST /internal/observatory/material-delta/consume` — 200.
 *
 * `count` is the accumulator value that was actually reset, read by the
 * compare-and-set attempt that won. A tick incrementing mid-consume forces a
 * retry whose read includes that increment, so it is counted rather than lost.
 */
export const ConsumeMaterialDeltaResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  etag: z.string().nullable()
})
export type ConsumeMaterialDeltaResponse = z.infer<typeof ConsumeMaterialDeltaResponseSchema>

export const ConsumeMaterialDeltaRequestSchema = LedgerKeyQuerySchema
export type ConsumeMaterialDeltaRequest = z.infer<typeof ConsumeMaterialDeltaRequestSchema>

// ── The sorting loop's shapes (plan 2026-09-07 U1, R1/R4/R15, KTD1–KTD3) ─────
//
// Two different postures live below, and mixing them up is the mistake to avoid.
//
// `ClusterProjectionSchema` is a READ shape over a key Scotty owns. It is loose
// on purpose: a cluster carries bookkeeping this plan has never heard of, and a
// stripping parse would let any loop module that round-trips a cluster erase it.
// The filing endpoint (U4) applies its own STRICT request schema, so R4's
// refusals do not depend on this one being tight.
//
// The `Loop*` shapes are WRITE shapes over the loop's own key. They are still
// loose at the record level — the loop is the single writer, but it is written
// incrementally across units, and a record a later unit extends must survive a
// read by an earlier one rather than being silently trimmed on the way back out.

/** The six buckets R1 fixes. One of these, exactly, on every cluster. */
export const OBSERVATORY_BUCKETS = ['customer', 'upstream', 'defect', 'config', 'efficiency', 'self'] as const
export const ObservatoryBucketSchema = z.enum(OBSERVATORY_BUCKETS)
export type ObservatoryBucket = z.infer<typeof ObservatoryBucketSchema>

/**
 * How a cluster came by its bucket (KTD1). The ledger is the authority, so a
 * `linear-label` source records that we adopted what the label said — it does
 * not make the label authoritative afterwards.
 */
export const ClusterBucketSourceSchema = z.enum(['scotty', 'linear-label'])
export type ClusterBucketSource = z.infer<typeof ClusterBucketSourceSchema>

/**
 * One cluster as the loop reads it: the `clusters` map key merged onto the entry
 * Scotty wrote. `clusterKey` is therefore required even though the stored entry
 * may not carry it — the caller supplies it from the map key, which is the only
 * place it is guaranteed to exist.
 *
 * Everything else is optional because the pre-U7 ledger has clusters that have
 * never been bucketed and clusters that have never been filed; a required
 * `bucket` here would fail to read the very ledger U7 exists to reconcile.
 */
export const ClusterProjectionSchema = z
  .object({
    clusterKey: z.string().min(1),
    bucket: ObservatoryBucketSchema.optional(),
    bucketAssignedAt: z.string().optional(),
    bucketSource: ClusterBucketSourceSchema.optional(),
    ticketRef: z.object({ identifier: z.string() }).loose().nullish(),
    fingerprints: z.array(z.string()).optional(),
    lastPromotedAt: z.string().nullish()
  })
  .loose()
export type ClusterProjection = z.infer<typeof ClusterProjectionSchema>

/**
 * Why a filing was refused (R4, KTD1). A closed set because the digest
 * classifies refusals by it — `AMBIGUOUS_BUCKET` is KTD1's two-labels case,
 * which is a recorded refusal rather than a coin flip.
 */
export const LoopRefusalReasonSchema = z.enum(['MISSING_BUCKET', 'UNKNOWN_BUCKET', 'AMBIGUOUS_BUCKET', 'HOME_MISMATCH'])
export type LoopRefusalReason = z.infer<typeof LoopRefusalReasonSchema>

/**
 * One refused filing, kept for 14 days so the next digest can surface it.
 *
 * `bucket` is a bare string, not the enum: the whole point of `UNKNOWN_BUCKET`
 * is that the caller sent something outside the set, and a refusal record that
 * cannot hold what was actually sent tells the operator nothing.
 */
export const LoopRefusalSchema = z
  .object({
    at: z.string(),
    clusterKey: z.string().min(1),
    bucket: z.string().nullish(),
    reason: LoopRefusalReasonSchema,
    requestedHome: z.string().nullish(),
    expectedHome: z.string().nullish()
  })
  .loose()
export type LoopRefusal = z.infer<typeof LoopRefusalSchema>

/**
 * One digest cadence's day claim. `claimedOn` is a local calendar day
 * (`YYYY-MM-DD`), not an instant: it is what makes "the daily digest posts once
 * a day" checkable by a job that may fire more than once.
 *
 * `postedAt: null` means claimed but not yet posted — the state a crashed run
 * leaves behind, and the reason claiming and posting are separate fields.
 */
export const LoopDigestClaimSchema = z
  .object({
    claimedOn: z.string().min(1),
    claimedAt: z.string().optional(),
    postedAt: z.string().nullish()
  })
  .loose()
export type LoopDigestClaim = z.infer<typeof LoopDigestClaimSchema>

export const LoopDigestSchema = z
  .object({
    daily: LoopDigestClaimSchema.optional(),
    weekly: LoopDigestClaimSchema.optional()
  })
  .loose()
export type LoopDigest = z.infer<typeof LoopDigestSchema>

/**
 * Where a human touch was observed (R15). `stale` is not a touch — it records
 * that the resolver could not read the ticket this pass, so the clock held
 * rather than advancing on missing evidence.
 *
 * Agent activity is deliberately unspellable here: Scotty, Raven and the loop
 * never reset the clock, so there is no value that could record them.
 */
export const LoopTouchSourceSchema = z.enum(['comment', 'history', 'watcher-note', 'stale'])
export type LoopTouchSource = z.infer<typeof LoopTouchSourceSchema>

/** What the operator did with a finding that came up for review (R16). */
export const LoopReviewSchema = z
  .object({
    action: z.enum(['extend', 'escalate', 'dismiss']),
    at: z.string(),
    by: z.string(),
    reason: z.string().optional()
  })
  .loose()
export type LoopReview = z.infer<typeof LoopReviewSchema>

/**
 * One fingerprint's ageing record (KTD3). Rewritten wholesale each daily pass
 * from the findings that are currently hot, so a record's absence means the
 * finding is no longer hot — not that it was never aged.
 */
export const LoopAgingRecordSchema = z
  .object({
    clusterKey: z.string().min(1),
    ongoingSince: z.string(),
    lastHumanTouchAt: z.string().nullish(),
    touchSource: LoopTouchSourceSchema.nullish(),
    knownAt: z.string().nullish(),
    reviewDueAt: z.string().nullish(),
    review: LoopReviewSchema.optional()
  })
  .loose()
export type LoopAgingRecord = z.infer<typeof LoopAgingRecordSchema>

/**
 * The whole `loop` top-level key (KTD2).
 *
 * The three collections default rather than being optional: the first run reads
 * `loop: {}` (or no key at all), and every caller would otherwise repeat the
 * same `?? []` guard. Defaulting on READ is safe because the loop replaces its
 * own key wholesale on write — there is no other writer whose absent field a
 * materialized default could overwrite.
 */
export const LoopKeySchema = z
  .object({
    refusals: z.array(LoopRefusalSchema).default([]),
    digest: LoopDigestSchema.default({}),
    aging: z.record(z.string(), LoopAgingRecordSchema).default({})
  })
  .loose()
export type LoopKey = z.infer<typeof LoopKeySchema>

// ── The cluster filing endpoint (plan 2026-09-07 U4, R3/R4/R5/R9, KTD4) ──────
//
// `POST /internal/observatory/cluster-ticket` is the ONLY path a cluster ticket
// is filed on, and it refuses rather than corrects (R4). These are the request
// and response shapes it speaks; the routing table itself (bucket → project,
// team, label) is backend-side, because a client copy of it would be a second
// authority the endpoint could not enforce against.

/**
 * A cluster key is stamped into the ticket body as `[cluster:<key>]` — the
 * marker the dedup search and every later reconcile matches on. It is therefore
 * NOT free text: a key carrying a bracket, a backtick or a newline would either
 * break the marker line or forge a second one, so the character set is closed
 * here rather than escaped downstream.
 */
export const CLUSTER_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@/-]{0,119}$/

/**
 * The seven parts R9 requires of a dispatch prompt. Every field is OPTIONAL in
 * the schema and required by the endpoint, on purpose: a missing part must come
 * back as `PROMPT_INCOMPLETE` naming the part, not as a generic body-parse 400
 * that tells the caller only that something, somewhere, was wrong.
 *
 * `acceptanceCheck` and `constraints` are one part each — they carry two fields
 * because R9 names two things inside each ("the test command that must pass and
 * the production signal that proves it"; "what must not break, what is out of
 * scope"), and a prompt with half of either is not an acceptance check.
 */
export const DispatchPromptSchema = z.object({
  /** What is going wrong, in the words the operator would recognise. */
  symptom: z.string().optional(),
  /** The query or log search that re-observes it. */
  reObserve: z.string().optional(),
  /** The files a fix would touch. */
  filesInScope: z.array(z.string()).optional(),
  /** The suspected cause — rendered as a GUESS, never as a verified fact. */
  suspectedCause: z.string().optional(),
  /** The shape a fix would take — also a guess. */
  fixShape: z.string().optional(),
  acceptanceCheck: z
    .object({
      /** The test command that must pass. */
      testCommand: z.string().optional(),
      /** The production signal that proves the fix landed. */
      productionSignal: z.string().optional()
    })
    .optional(),
  constraints: z
    .object({
      mustNotBreak: z.string().optional(),
      outOfScope: z.string().optional()
    })
    .optional()
})
export type DispatchPrompt = z.infer<typeof DispatchPromptSchema>

/**
 * The blast-radius facts (R8/R9). These are counters the observer measured, so
 * the composed body renders them under a "Verified" heading — the other half of
 * R9's "verified facts and guesses are marked as such", whose guess half is the
 * suspected cause and fix shape.
 *
 * `schools` and `syncTypes` are optional because a `self`-bucket finding about
 * the Observatory's own detectors has neither.
 */
export const ClusterBlastRadiusSchema = z.object({
  schools: z.number().int().nonnegative().optional(),
  syncTypes: z.array(z.string().min(1)).optional(),
  runsSeen: z.number().int().nonnegative(),
  firstSeen: z.string().min(1),
  lastSeen: z.string().min(1).optional()
})
export type ClusterBlastRadius = z.infer<typeof ClusterBlastRadiusSchema>

/**
 * What an evidence-only filing carries besides the symptom and the re-observe
 * query (signal-triage KTD11). Every field is a measurement or a deterministic
 * verdict: the policy's severity and trace, the cause label Jev gave the lead
 * signal, the sources that saw it and the log-index code locations. Nothing
 * here is a diagnosis; that comes later from the triage module.
 */
export const ClusterEvidenceSchema = z.object({
  /** The Issue's latched episode severity (KTD4). */
  severity: z.string().min(1).max(16),
  /** Jev's cause label for the lead signal: a classifier label, not a diagnosis. */
  cause: z.string().min(1).max(32),
  sources: z.array(z.string().min(1).max(32)).max(10),
  /** The policy rules that fired for the lead signal, in order (R9). */
  trace: z.array(z.object({ ruleId: z.string().min(1).max(64), detail: z.string().max(500) })).max(20),
  /** `file:line (operation)` from the build-time log index (R6). */
  codeLocations: z.array(z.string().min(1).max(300)).max(20),
  /** Paying schools involved, by id (never names). */
  payingSchoolIds: z.array(z.number().int().positive()).max(50),
  /** Earlier tickets for the same Issue, e.g. the Done ticket a refile follows (R14). */
  relatedTickets: z.array(z.string().min(1).max(64)).max(10)
})
export type ClusterEvidence = z.infer<typeof ClusterEvidenceSchema>

/**
 * `POST /internal/observatory/cluster-ticket`.
 *
 * `bucket` is a bare string, not {@link ObservatoryBucketSchema}, and it is
 * optional. That is deliberate and is what makes R4 implementable: "filing with
 * no bucket" and "filing with an unknown bucket" are refusals the digest has to
 * be able to REPORT, and a schema that rejected them here would collapse both
 * into an unrecorded 400. The endpoint validates the value against the closed
 * set and records what was actually sent.
 *
 * `requestedProjectId` / `requestedTeamId` are what the CALLER believes the home
 * is. They are optional — omitting them lets the registry route the filing, which
 * is the intended path — and when present they are checked against the bucket's
 * real home and refused on a mismatch (AE3). They are never used to file.
 */
export const FileClusterTicketRequestSchema = z
  .object({
    /** Agent identity the issue is authored as. */
    label: z.string().min(1).default('scotty'),
    clusterKey: z.string().regex(CLUSTER_KEY_PATTERN),
    bucket: z.string().optional(),
    title: z.string().min(1).max(255),
    dispatchPrompt: DispatchPromptSchema,
    /** Linear's scale: 0 none, 1 urgent … 4 low. */
    priority: z.number().int().min(0).max(4),
    blastRadius: ClusterBlastRadiusSchema,
    /** The home the caller thinks this bucket has — checked, never used to file. */
    requestedProjectId: z.string().min(1).optional(),
    requestedTeamId: z.string().min(1).optional(),
    /** Compose and resolve, but make no Linear call and file nothing. */
    dryRun: z.boolean().default(false),
    /**
     * File without a diagnosis (signal-triage KTD11): only `symptom` and
     * `reObserve` are required, the cause and fix parts are never rendered, and
     * the ticket carries the `needs-investigation` label for the triage module.
     */
    evidenceOnly: z.boolean().default(false),
    evidence: ClusterEvidenceSchema.optional()
  })
  .strict()
export type FileClusterTicketRequest = z.infer<typeof FileClusterTicketRequestSchema>

/** Where the filing resolved to, read off the backend's bucket registry (R3). */
export const FileClusterTicketHomeSchema = z.object({
  bucket: ObservatoryBucketSchema,
  projectId: z.string(),
  teamId: z.string(),
  labelId: z.string(),
  countsTowardWip: z.boolean()
})
export type FileClusterTicketHome = z.infer<typeof FileClusterTicketHomeSchema>

/** The ticket a filing produced — or adopted, when the marker search found one. */
export const FiledClusterIssueSchema = z.object({
  id: z.string(),
  identifier: z.string(),
  url: z.string().nullable()
})
export type FiledClusterIssue = z.infer<typeof FiledClusterIssueSchema>

/**
 * 200 from the filing endpoint.
 *
 * `filed` and `deduped` are separate facts, not one enum: `filed: false,
 * deduped: true` is an earlier ticket adopted, `filed: false, deduped: false` is
 * a dry run, and `filed: true` is a new ticket. Collapsing them would leave the
 * caller unable to tell an adoption from a rehearsal.
 *
 * `stateId` is null on a dry run — resolving the team's Backlog state is a Linear
 * read, and a dry run makes no Linear call at all.
 */
export const FileClusterTicketResponseSchema = z.object({
  filed: z.boolean(),
  deduped: z.boolean(),
  dryRun: z.boolean(),
  clusterKey: z.string(),
  home: FileClusterTicketHomeSchema,
  stateId: z.string().nullable(),
  /** The `[cluster:<key>]` line the body ends with, and the dedup search term. */
  marker: z.string(),
  /** The composed, escaped description — the whole point of a dry run. */
  body: z.string(),
  issue: FiledClusterIssueSchema.nullable()
})
export type FileClusterTicketResponse = z.infer<typeof FileClusterTicketResponseSchema>

export * from './signals'
export * from './signals-eval'
